import { useState, useEffect, useContext, useMemo } from 'react'
import { Box } from '@mui/material'
import { fetchData } from '../../../global/utils/fetch'
import { getCarpetChartData } from './api'
import {
  CarpetChartData,
  CarpetChartDayRow,
} from '../../../../../app/types/carpet-chart'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'
import Dashboard, {
  DashboardColumn,
} from '../../../global/components/dashboard/Dashboard'
import formatCurrency from '../../../global/utils/format-currency-usd'
import PrimaryDropdown from '../../../global/components/dropdowns/PrimaryDropdown'
import { MONTH } from '../../../global/constants/date'

/**
 * Generate list of last 12 months in "Month Year" format
 * Returns months in reverse chronological order (most recent first)
 */
function getLastTwelveMonths(): {
  label: string
  month: number
  year: number
}[] {
  const currentDate = new Date()
  const months: { label: string; month: number; year: number }[] = []

  for (let i = 0; i < 12; i++) {
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - i,
      1,
    )
    const monthIndex = date.getMonth()
    const year = date.getFullYear()
    months.push({
      label: `${MONTH[monthIndex]} ${year}`,
      month: monthIndex + 1, // MONTH enum is 1-indexed
      year,
    })
  }

  return months
}

// Background colors for case count cells - use CSS variables for theme support
// These will be resolved at render time from the theme-variables.css
const getCaseCountColor = (type: 'green' | 'yellow' | 'red'): string => {
  // Access CSS variables from the document
  const root = document.documentElement
  const computedStyle = getComputedStyle(root)

  switch (type) {
    case 'green':
      return computedStyle.getPropertyValue('--chart-green').trim() || '#90EE90'
    case 'yellow':
      return computedStyle.getPropertyValue('--chart-yellow').trim() || '#FFFF99'
    case 'red':
      return computedStyle.getPropertyValue('--chart-red').trim() || '#FF9999'
  }
}

/**
 * Get background color for Portal or Roadie case counts
 * 0 = green, 1-2 = yellow, 3+ = red
 */
function getPortalRoadieBackgroundColor(count: number): string {
  if (count === 0) return getCaseCountColor('green')
  if (count <= 2) return getCaseCountColor('yellow')
  return getCaseCountColor('red')
}

/**
 * Get background color for RiskConnect case counts
 * 0 = green, 1 = yellow, 2+ = red
 */
function getRiskConnectBackgroundColor(count: number): string {
  if (count === 0) return getCaseCountColor('green')
  if (count === 1) return getCaseCountColor('yellow')
  return getCaseCountColor('red')
}

/**
 * Get background color for total damage cost
 * <100 = green, 100-500 = yellow, >500 = red
 */
function getTotalCostBackgroundColor(cost: number): string {
  if (cost < 100) return getCaseCountColor('green')
  if (cost <= 500) return getCaseCountColor('yellow')
  return getCaseCountColor('red')
}

function CarpetChart() {
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [dashboardData, setDashboardData] = useState<CarpetChartData | null>(
    null,
  )

  const lastTwelveMonths = useMemo(() => getLastTwelveMonths(), [])
  const [selectedMonthLabel, setSelectedMonthLabel] = useState<string>(
    lastTwelveMonths[0].label,
  )

  // Get month and year from selected label
  const selectedMonth = useMemo(() => {
    return (
      lastTwelveMonths.find((m) => m.label === selectedMonthLabel) ??
      lastTwelveMonths[0]
    )
  }, [selectedMonthLabel, lastTwelveMonths])

  async function fetchDashboard() {
    setIsLoading(true)
    try {
      const data = await fetchData<CarpetChartData>(
        getCarpetChartData(selectedMonth.month, selectedMonth.year),
      )
      setDashboardData(data)
    } catch (err) {
      console.error(err)
      setDashboardData(null)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Failed to load carpet chart data',
        severity: 'error',
      })
    }
    setIsLoading(false)
  }

  useEffect(() => {
    fetchDashboard()
  }, [selectedMonth])

  const columns: DashboardColumn<CarpetChartDayRow>[] = useMemo(
    () => [
      {
        Header: 'Date',
        accessor: 'date',
        tooltip: 'Date (MM/DD/YYYY)',
      },
      {
        Header: 'Day',
        accessor: 'dayOfWeek',
        tooltip: 'Day of the week',
      },
      {
        Header: 'Portal Cases',
        accessor: 'portalCasesCount',
        tooltip:
          'Cases that are neither Roadie nor RiskConnect. Color: 0 = green, 1-2 = yellow, 3+ = red',
        align: 'center' as const,
        Cell: ({ value }) => ({
          value,
          bold: true,
          backgroundColor: getPortalRoadieBackgroundColor(value as number),
        }),
      },
      {
        Header: 'Roadie Cases',
        accessor: 'roadieCasesCount',
        tooltip:
          'Cases that are Roadie. Color: 0 = green, 1-2 = yellow, 3+ = red',
        align: 'center' as const,
        Cell: ({ value }) => ({
          value,
          bold: true,
          backgroundColor: getPortalRoadieBackgroundColor(value as number),
        }),
      },
      {
        Header: 'RiskConnect Cases',
        accessor: 'riskConnectCasesCount',
        tooltip:
          'Cases that are RiskConnect. Color: 0 = green, 1 = yellow, 2+ = red',
        align: 'center' as const,
        Cell: ({ value }) => ({
          value,
          bold: true,
          backgroundColor: getRiskConnectBackgroundColor(value as number),
        }),
      },
      {
        Header: 'Installations',
        accessor: 'installationCount',
        tooltip: 'Number of completed installations',
        align: 'center' as const,
      },
      {
        Header: 'Total Cases',
        accessor: 'casesCount',
        tooltip: 'Total count of all cases',
        align: 'center' as const,
      },
      {
        Header: '% Cases/Orders',
        accessor: 'percentCasesPerDay',
        tooltip: 'Percentage of cases of all completed orders',
        align: 'center' as const,
        Cell: ({ value }) => ({
          value: `${(value as number).toFixed(2)}%`,
        }),
      },
      {
        Header: 'Portal Cost',
        accessor: 'portalCasesCost',
        tooltip: 'Sum of damage cost for Portal cases',
        align: 'right' as const,
        Cell: ({ value }) => ({
          value: formatCurrency(value as number),
        }),
      },
      {
        Header: 'Roadie Cost',
        accessor: 'roadieCasesCost',
        tooltip: 'Sum of damage cost for Roadie cases',
        align: 'right' as const,
        Cell: ({ value }) => ({
          value: formatCurrency(value as number),
        }),
      },
      {
        Header: 'RiskConnect Cost',
        accessor: 'riskConnectCasesCost',
        tooltip: 'Sum of damage cost for RiskConnect cases',
        align: 'right' as const,
        Cell: ({ value }) => ({
          value: formatCurrency(value as number),
        }),
      },
      {
        Header: 'Total Cost',
        accessor: 'totalDamageCost',
        tooltip:
          'Sum of all damage costs. Color: <$100 = green, $100-$500 = yellow, >$500 = red',
        align: 'right' as const,
        Cell: ({ value }) => ({
          value: formatCurrency(value as number),
          bold: true,
          backgroundColor: getTotalCostBackgroundColor(value as number),
        }),
      },
    ],
    [],
  )

  const totals = useMemo(() => {
    if (!dashboardData) return undefined
    const t = dashboardData.totals
    return [
      // Skip date column (handled by totalsLabel), include dayOfWeek placeholder
      '',
      t.portalCasesCount,
      t.roadieCasesCount,
      t.riskConnectCasesCount,
      t.installationCount,
      t.casesCount,
      `${t.percentCasesPerDay.toFixed(2)}%`,
      formatCurrency(t.portalCasesCost),
      formatCurrency(t.roadieCasesCost),
      formatCurrency(t.riskConnectCasesCost),
      formatCurrency(t.totalDamageCost),
    ]
  }, [dashboardData])

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 2,
          alignItems: 'center',
        }}
      >
        <PrimaryDropdown
          label='Month'
          items={lastTwelveMonths.map((m) => ({
            key: m.label,
            value: m.label,
          }))}
          defaultValue={selectedMonthLabel}
          onChange={setSelectedMonthLabel}
          disabled={isLoading}
        />
      </Box>
      <Dashboard<CarpetChartDayRow>
        columns={columns}
        data={dashboardData?.rows ?? []}
        isLoading={isLoading}
        totals={totals}
        totalsLabel='Total'
        getRowKey={(row) => row.date}
        noDataMessage='No carpet chart data available.'
        maxHeight='70vh'
      />
    </Box>
  )
}

export default CarpetChart
